import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useRef, useEffect } from 'react';

/**
 * ProtectedRoute - Protects routes that require authentication
 * Redirects to login if no token is found
 */
const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const token = localStorage.getItem('token');
    const redirectRef = useRef(null);

    // Subscribe to Redux state
    const { data: userData } = useSelector((state) => state.getCurrentUserDetails);

    const userType = userData?.account_type || localStorage.getItem('userType');
    const status = userData?.status || localStorage.getItem('isVerified');
    const phoneVerified = userData?.phone_verified;
    const rawOnboardingCompleted = userData?.onboarding_completed;
    const isOnboardingComplete = rawOnboardingCompleted === true || localStorage.getItem('onboardingCompleted') === 'true';

    const rawStep = userData?.current_step;
    const currentStep = (rawStep !== undefined && rawStep !== null)
        ? (typeof rawStep === 'string' ? (parseInt(rawStep.replace(/[^0-9]/g, ''), 10) || 1) : rawStep)
        : (parseInt(localStorage.getItem('currentStep'), 10) || 1);


    // Track redirects to prevent infinite loops
    useEffect(() => {
        redirectRef.current = null;
    }, [location.pathname]);

    if (!token) {
        // No token found, redirect to login
        if (redirectRef.current !== '/') {
            redirectRef.current = '/';
            return <Navigate to="/" replace />;
        }
    }

    // Verify phone before allowing access to protected routes
    if (phoneVerified === false) {
        if (redirectRef.current !== '/verify-otp') {
            redirectRef.current = '/verify-otp';
            sessionStorage.setItem('sessionExpired', 'Your account verification is pending. Please verify your OTP to continue.');
            return <Navigate to="/" replace />;
        }
    }

    // Business workflow enforcing
    if (userType === 'business') {
        // 1. If approved and onboarding is complete, redirect AWAY from onboarding/verification if they try to go there
        if (status === 'approved' && (isOnboardingComplete || currentStep >= 7)) {
            if (location.pathname === '/onboarding' || location.pathname === '/verification-in-progress') {
                if (redirectRef.current !== '/dashboard') {
                    redirectRef.current = '/dashboard';
                    return <Navigate to="/dashboard" replace />;
                }
            }
        }

        const rawIsNewUser = userData?.is_new_business_user;
        const isNewBusinessUser = rawIsNewUser !== undefined ? rawIsNewUser : localStorage.getItem('isNewBusinessUser') === 'true';

        // 2. If submitted (pending review), force verification screen
        if (status === 'submitted' && location.pathname !== '/verification-in-progress') {
            if (redirectRef.current !== '/verification-in-progress') {
                redirectRef.current = '/verification-in-progress';
                return <Navigate to="/verification-in-progress" replace />;
            }
        }

        // 3. Soft Onboarding Force Redirect (only if not already redirecting to onboarding)
        if (currentStep < 2 && location.pathname !== '/onboarding' && redirectRef.current !== '/onboarding') {
            redirectRef.current = '/onboarding';
            return <Navigate to="/onboarding" state={{ step: currentStep, freshEntry: true }} replace />;
        }

        // 3a. If rejected, allow /onboarding for re-submission, otherwise show rejection screen
        if (status === 'rejected') {
            if (location.pathname !== '/verification-in-progress' && location.pathname !== '/onboarding') {
                if (redirectRef.current !== '/verification-in-progress') {
                    redirectRef.current = '/verification-in-progress';
                    return <Navigate to="/verification-in-progress" replace />;
                }
            }
        }
    }

    // Normal User restrictions
    if (userType === 'user') {
        // Prevent normal users from accessing business onboarding or verification screens
        if (location.pathname === '/onboarding' || location.pathname === '/verification-in-progress') {
            if (redirectRef.current !== '/dashboard') {
                redirectRef.current = '/dashboard';
                return <Navigate to="/dashboard" replace />;
            }
        }
    }

    // Token exists and onboarding is satisfied, render the protected component
    return children;
};

export default ProtectedRoute;
